import type { Application } from "@/lib/api/envoy";
import { usePosting, usePostingEvents } from "@/lib/hooks/catalogue";
import { useApplicationEvents, useApplicationFit, useApplicationOf } from "@/lib/hooks/envoy";
import { useModels } from "@/lib/hooks/gateway";
import { usePostingFits, useTailoredLink } from "@/lib/hooks/resume";
import { postingRecord, resumesWritten, sentOf } from "@/lib/posting";
import { modelNamer, shownFit } from "@/lib/posting/page";

/**
 * What the posting page reads: the posting from the catalogue, and for a
 * member its record, the application, its fit and events, the fit now
 * when there is no application, and the resume that went.
 */
export function usePostingDetail(postingId: string, known: boolean, member: boolean) {
    const posting = usePosting(known ? postingId : null);
    // A posting's record needs a sign in, and is the person's own as much as
    // the catalogue's, so a visitor neither asks for it nor sees it.
    const postingEvents = usePostingEvents(known && member ? postingId : null);
    const found = useApplicationOf(known && member ? postingId : null);
    const application: Application | null = found.data ?? null;
    const applicationId = application?.application_id ?? null;
    const matchFit = useApplicationFit(applicationId);
    const events = useApplicationEvents(applicationId);
    const fitsNow = usePostingFits(found.isSuccess && !application ? [postingId] : []);
    const models = useModels();
    const applied = application?.status === "APPLIED";
    const sent = application && applied ? sentOf(application, events.data ?? []) : null;
    const sentLink = useTailoredLink(sent?.resumeId ?? null);
    const modelName = modelNamer(models.data?.models);
    return {
        posting,
        /** Whether the application has been asked for and answered, with or without one. */
        answered: found.isSuccess,
        /** Whether asking for the application failed, so the page stops waiting on it. */
        applicationFailed: found.isError,
        application,
        applied,
        sent,
        sentLink: sentLink.data,
        modelName,
        fit: shownFit(application, matchFit.data, fitsNow.data, postingId),
        fitLoading: application ? matchFit.isPending : fitsNow.isPending,
        scoredAt: matchFit.data?.scored_at,
        written: resumesWritten(events.data ?? [], application?.resume_id),
        record: postingRecord(postingEvents.data ?? [], events.data ?? [], modelName),
        recordError: postingEvents.isError ? postingEvents.error : null,
    };
}
